import { claimflowApiClient } from './claimflowApiClient';

/**
 * Comptabilité (claim accounting movements) API service — mahhp 22853.
 *
 * One read-only call through origami's generic ERP proxy
 * (`/claimflow/erp/registries/veos/endpoints/{slug}`), which wraps the
 * `ErpProxyResponse<T>` envelope around the raw VEOS payload. The slug
 * `boby-ws2-gs-tab-comptabilite` maps to `GET /boBy/v2/WS2_GS_TAB_COMPTABILITE`
 * (query `id_sin` + `perPage`), which returns the standard list envelope
 * `{ total, perPage, page, totalPage, from, to, data }`.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

/** The standard VEOS list envelope (`data` may be omitted on an empty result). */
interface VeosListEnvelope<T> {
  total?: number;
  perPage?: number;
  page?: number;
  totalPage?: number;
  from?: number | null;
  to?: number | null;
  data?: T[] | null;
}

/**
 * A row from `WS2_GS_TAB_COMPTABILITE`: one accounting movement of the claim
 * (règlement, recours, …). Dates are epoch milliseconds; every field but the
 * id and the amount may be `null` on VEOS' side.
 */
export interface ComptabiliteRow {
  /** Movement id — the row's stable key. */
  ID_SRG: number;
  /** Direction / origin of the movement (the "Sens" column, e.g. `REGLEMENT`). */
  ORIGINE: string | null;
  /** Creation date of the movement, epoch ms (the "Date" column). */
  DTCRE_SRG: number | null;
  /** Beneficiary category code (e.g. `EXPERT`). */
  CD_CATEG: string | null;
  /** Beneficiary name. */
  NOM_PER: string | null;
  /** Movement label. */
  LIB_SRG: string | null;
  /** Amount, in euros. */
  MNT_SRG: number | null;
  /** Bordereau reference. */
  IDR_BOR: string | null;
  /** Bordereau id. */
  ID_BOR: string | null;
  /** Cheque letter date, epoch ms. */
  DATE_LCHQ: number | null;
  /** Cheque letter generation date, epoch ms. */
  DATGEN_LCHQ: number | null;
  /** Operation date, epoch ms. */
  DATO_OPER: number | null;
  /** Second operation date, epoch ms. */
  DATO_OPER2: number | null;
  /** Code of the user who recorded the movement (the "Util" column). */
  CD_UTIL: string | null;
}

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

export const comptabiliteService = {
  /**
   * List a claim's accounting movements (`WS2_GS_TAB_COMPTABILITE`), in the
   * order VEOS returns them.
   *
   * `perPage: 9999` overrides VEOS's default pagination (perPage=15), which
   * would otherwise truncate the list — the codebase convention for full lists.
   */
  async fetchComptabilite(idSin: string | number): Promise<ComptabiliteRow[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosListEnvelope<ComptabiliteRow> | null>
    >('/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-comptabilite', {
      params: { id_sin: idSin, perPage: 9999 },
    });
    return unwrap(response.data)?.data ?? [];
  },
};
